import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  applyDocumentLang,
  readLocaleFromLocation,
  readSharedLocaleCookie,
  writeSharedLocaleCookie,
} from '../lib/persist-locale';
import {
  isSupportedLanguage,
  LOCALE_STORAGE_KEY,
  SHARED_LOCALE_COOKIE,
  SUPPORTED_LANGUAGES,
} from './languages';

import enAuditLog from '../locales/en/audit-log.json';
import enAuth from '../locales/en/auth.json';
import enCabinet from '../locales/en/cabinet.json';
import enCommon from '../locales/en/common.json';
import enInvoicePay from '../locales/en/invoice-pay.json';
import enSecurity from '../locales/en/security.json';
import enRbac from '../locales/en/rbac.json';
import enAml from '../locales/en/aml-ui.json';
import enWorkspace from '../locales/en/workspace.json';

import ruAuditLog from '../locales/ru/audit-log.json';
import ruAuth from '../locales/ru/auth.json';
import ruCabinet from '../locales/ru/cabinet.json';
import ruCommon from '../locales/ru/common.json';
import ruInvoicePay from '../locales/ru/invoice-pay.json';
import ruSecurity from '../locales/ru/security.json';
import ruRbac from '../locales/ru/rbac.json';
import ruAml from '../locales/ru/aml-ui.json';
import ruWorkspace from '../locales/ru/workspace.json';

import esAuditLog from '../locales/es/audit-log.json';
import esAuth from '../locales/es/auth.json';
import esCabinet from '../locales/es/cabinet.json';
import esCommon from '../locales/es/common.json';
import esInvoicePay from '../locales/es/invoice-pay.json';
import esSecurity from '../locales/es/security.json';
import esAml from '../locales/es/aml-ui.json';
import esWorkspace from '../locales/es/workspace.json';

import frAuditLog from '../locales/fr/audit-log.json';
import frAuth from '../locales/fr/auth.json';
import frCabinet from '../locales/fr/cabinet.json';
import frCommon from '../locales/fr/common.json';
import frInvoicePay from '../locales/fr/invoice-pay.json';
import frSecurity from '../locales/fr/security.json';
import frAml from '../locales/fr/aml-ui.json';
import frWorkspace from '../locales/fr/workspace.json';

import ptAuditLog from '../locales/pt/audit-log.json';
import ptAuth from '../locales/pt/auth.json';
import ptCabinet from '../locales/pt/cabinet.json';
import ptCommon from '../locales/pt/common.json';
import ptInvoicePay from '../locales/pt/invoice-pay.json';
import ptSecurity from '../locales/pt/security.json';
import ptAml from '../locales/pt/aml-ui.json';
import ptWorkspace from '../locales/pt/workspace.json';

import deAuditLog from '../locales/de/audit-log.json';
import deCommon from '../locales/de/common.json';
import deSecurity from '../locales/de/security.json';
import deRbac from '../locales/de/rbac.json';

export { SUPPORTED_LANGUAGES };
export type { SupportedLanguage } from './languages';

const nestEvents = (labels: Record<string, string>): Record<string, unknown> => {
  const events: Record<string, unknown> = {};
  Object.entries(labels).forEach(([key, value]) => {
    const parts = key.split('.');
    let cursor: Record<string, unknown> = events;
    parts.forEach((part, index) => {
      if (index === parts.length - 1) {
        cursor[part] = value;
        return;
      }
      if (typeof cursor[part] !== 'object' || cursor[part] === null) {
        cursor[part] = {};
      }
      cursor = cursor[part] as Record<string, unknown>;
    });
  });
  return events;
};

const deepMerge = (
  base: Record<string, unknown>,
  extra: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = { ...base };
  Object.entries(extra).forEach(([key, value]) => {
    const current = result[key];
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      current &&
      typeof current === 'object' &&
      !Array.isArray(current)
    ) {
      result[key] = deepMerge(current as Record<string, unknown>, value as Record<string, unknown>);
      return;
    }
    result[key] = value;
  });
  return result;
};

const mergeAudit = <T extends object>(
  cabinet: T,
  audit: typeof enAuditLog,
  security: typeof enSecurity
) => {
  const { eventLabels, ...rest } = audit;
  return {
    ...cabinet,
    auditLog: {
      ...rest,
      events: nestEvents(eventLabels),
    },
    security,
  };
};

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    cabinet: deepMerge(deepMerge(mergeAudit(enCabinet, enAuditLog, enSecurity), enAml), enWorkspace),
    rbac: enRbac,
    'invoice-pay': enInvoicePay,
  },
  ru: {
    common: ruCommon,
    auth: ruAuth,
    cabinet: deepMerge(deepMerge(mergeAudit(ruCabinet, ruAuditLog, ruSecurity), ruAml), ruWorkspace),
    rbac: ruRbac,
    'invoice-pay': ruInvoicePay,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    cabinet: deepMerge(deepMerge(mergeAudit(esCabinet, esAuditLog, esSecurity), esAml), esWorkspace),
    'invoice-pay': esInvoicePay,
  },
  fr: {
    common: frCommon,
    auth: frAuth,
    cabinet: deepMerge(deepMerge(mergeAudit(frCabinet, frAuditLog, frSecurity), frAml), frWorkspace),
    'invoice-pay': frInvoicePay,
  },
  pt: {
    common: ptCommon,
    auth: ptAuth,
    cabinet: deepMerge(deepMerge(mergeAudit(ptCabinet, ptAuditLog, ptSecurity), ptAml), ptWorkspace),
    'invoice-pay': ptInvoicePay,
  },
  de: {
    common: deCommon,
    cabinet: mergeAudit({}, deAuditLog, deSecurity),
    rbac: deRbac,
  },
};

const fromQuery = readLocaleFromLocation();
const fromCookie = readSharedLocaleCookie();
const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
const initialLocale =
  fromQuery ?? fromCookie ?? (isSupportedLanguage(savedLocale) ? savedLocale : 'en');

localStorage.setItem(LOCALE_STORAGE_KEY, initialLocale);
localStorage.setItem(SHARED_LOCALE_COOKIE, initialLocale);
writeSharedLocaleCookie(initialLocale);

applyDocumentLang(initialLocale);

i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: ['common', 'auth', 'cabinet', 'invoice-pay', 'rbac'],
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
